import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Store, CalendarDays, PhoneCall, ArrowRight, TrendingUp, TrendingDown, Trophy,
  ListChecks, Zap, Copy, MapPin, Clock, AlertTriangle, Target, Map, Users, RefreshCw,
  Download, Loader2, FileSpreadsheet, Phone, Wallet,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip, LabelList,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageBody } from "@/components/AppShell";
import { KpiCard, Gauge, SkeletonCard } from "@/components/Kpi";
import { BulletinsLiveWidget } from "@/components/BulletinsLiveWidget";
import { TrendsSummaryWidget } from "@/components/TrendsSummaryWidget";
import { SPOT_STATUS, frenchDate } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SEV = {
  high: { label: "Prioritaire", color: "#EE722E", bg: "rgba(238,114,46,0.10)", border: "rgba(238,114,46,0.30)" },
  medium: { label: "À planifier", color: "#B98900", bg: "rgba(233,184,0,0.12)", border: "rgba(233,184,0,0.35)" },
  low: { label: "Hygiène", color: "rgba(24,24,24,0.55)", bg: "rgba(24,24,24,0.05)", border: "var(--border-soft)" },
};

// Statut de rotation d'une zone (rappel visuel territoires ↔ CDP).
const ZONE_STATUS = {
  occupee: { label: "Occupée", dot: "#1F9D55" },
  bientot_libre: { label: "Bientôt libre", dot: "#B98900" },
  a_renouveler: { label: "À renouveler", dot: "#D64545" },
  mutualisee: { label: "Mutualisée", dot: "#4338CA" },
  libre: { label: "Libre", dot: "rgba(24,24,24,0.4)" },
};

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [topflop, setTopflop] = useState(null);
  const [brief, setBrief] = useState(null);
  const [zonemap, setZonemap] = useState(null);
  const [telAlerts, setTelAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, d, tf, b, zm] = await Promise.all([
          api.get("/dashboard/overview"),
          api.get("/dashboard/decisions"),
          api.get("/dashboard/top-flop"),
          api.get("/dashboard/brief"),
          api.get("/dashboard/zone-map"),
        ]);
        setOv(o.data); setDecisions(d.data); setTopflop(tf.data); setBrief(b.data); setZonemap(zm.data);
      } finally { setLoading(false); }
    })();
  }, []);

  // Alertes téléphonie (cockpit) — réservées à l'admin.
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/calls/alerts").then((r) => setTelAlerts(r.data?.alerts || [])).catch(() => {});
  }, [user]);

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Bonjour" : h < 18 ? "Bon après-midi" : "Bonsoir";
  };

  const totals = ov?.totals || {};
  const fill = ov?.fill_rates || {};
  const pvb = ov?.paris_vs_banlieue || { paris: 0, banlieue: 0 };
  const totalSpots = totals.spots || 0;

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user?.name?.split(" ")[0]}`}
        subtitle={user?.role === "admin"
          ? "Votre cockpit de pilotage — les décisions de la semaine, classées par impact."
          : "Votre espace de travail — vos priorités et votre progression."}
        action={
          <Button onClick={() => navigate("/planning")} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="home-go-planning">
            <CalendarDays className="mr-2 h-4 w-4" /> Ouvrir le planning
          </Button>
        }
      />
      <PageBody>
        {loading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 @4xl/page:grid-cols-4">{[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}</div>
            <div className="h-64 animate-pulse rounded-2xl bg-white" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Alerte sauvegarde (admin) : une sauvegarde qui échoue en silence est le pire scénario */}
            {user?.role === "admin" && ov?.backup_alert?.stale && (
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.05)] p-4" data-testid="backup-alert-banner">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[rgba(214,69,69,0.12)]">
                    <AlertTriangle className="h-4 w-4 text-[#9B2226]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-[#9B2226]">
                      {ov.backup_alert.never ? "Aucune sauvegarde de la base n'a encore été réalisée" : "La dernière sauvegarde de la base date de plus de 24 h"}
                    </div>
                    <p className="mt-0.5 text-xs text-[rgba(24,24,24,0.66)]">
                      {ov.backup_alert.never
                        ? "Initialisez le filet de sécurité en lançant une première sauvegarde."
                        : `Dernière sauvegarde réussie il y a ${Math.round(ov.backup_alert.hours_since || 0)} h. Vérifiez le planificateur de sauvegarde.`}
                    </p>
                  </div>
                </div>
                <button onClick={() => navigate("/admin")} data-testid="backup-alert-action"
                  className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-xs font-bold text-[#9B2226] shadow-[var(--shadow-sm)] transition-transform hover:-translate-y-0.5">
                  Gérer les sauvegardes <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* KPI row (drill-down) */}
            <div className="grid grid-cols-2 gap-4 @4xl/page:grid-cols-4">
              <KpiCard label="Spots dans la banque" value={totalSpots} hint={`${totals.payants || 0} payants`} accent="var(--shariti-orange)" onClick={() => navigate("/spots")} testId="kpi-total-spots" info="Nombre total de spots présents dans la banque (tous statuts confondus)." />
              <KpiCard label="Spots actifs" value={totals.actifs_exploites || 0} suffix={`/ ${ov?.spots_actifs_target || 130}`} accent="#1F9D55" onClick={() => navigate("/planning")} testId="kpi-actifs" info="Spots réellement exploités : au moins un passage planifié ou réalisé sur les 30 derniers jours." />
              <KpiCard label="Spots planifiés (semaine)" value={ov?.journees_spot_week || 0} suffix={`/ ${ov?.journees_spot_target || 62}`} accent="#2F6FED" onClick={() => navigate("/planning")} testId="kpi-journees" info="Nombre de spots planifiés cette semaine (1 spot planifié sur une journée = 1 créneau). Objectif hebdomadaire à droite." />
              <KpiCard label="Impact potentiel" value={decisions?.total_impact || 0} suffix="bulletins" hint="à débloquer via les décisions" accent="var(--shariti-yellow)" onClick={() => navigate("/planning")} testId="kpi-impact" info="Estimation : bulletins additionnels potentiels si vous appliquez les décisions de la semaine (combler le planning, booker les accords, réactiver les dormants…). Chiffre projeté, non réalisé." />
            </div>

            {/* Bulletins en temps réel — cockpit dynamique (masquable par l'admin) */}
            {!((user?.hidden_features || []).includes("bulletins")) && (
              <BulletinsLiveWidget onOpenPointage={() => navigate("/pointage")} />
            )}

            {/* Résumé Tendances (admin) : évolution des taux de réussite */}
            {user?.role === "admin" && <TrendsSummaryWidget />}

            {/* Rappel territoires : qui occupe quelle zone ? */}
            <ZoneMapCard data={zonemap} navigate={navigate} isAdmin={user?.role === "admin"} />

            {/* Remplissage + Paris/Banlieue */}
            <div className="grid gap-4 @4xl/page:grid-cols-3">
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @4xl/page:col-span-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold">Remplissage du planning</h3>
                  <span className="text-xs text-muted-ink">Semaine {ov?.week_number}</span>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {["s1", "s2", "s3", "s4"].map((k, i) => (
                    <button key={k} onClick={() => navigate("/planning")} data-testid={`gauge-${k}`}>
                      <Gauge value={fill[k]?.rate || 0} target={fill[k]?.target || (i < 2 ? 100 : 70)}
                        label={`S+${i + 1}`} sublabel={`${fill[k]?.booked || 0}/${fill[k]?.capacity || 0}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="paris-banlieue">
                <h3 className="text-sm font-bold">Journées-spot : Paris vs banlieue</h3>
                <p className="text-[11px] text-muted-ink">Semaine en cours</p>
                <div className="mt-3 h-[150px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[{ name: "Paris (75)", value: pvb.paris, fill: "#EE722E" }, { name: "Banlieue", value: pvb.banlieue, fill: "#2F6FED" }]} margin={{ top: 12, right: 8, left: -20, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <RTooltip cursor={{ fill: "rgba(238,114,46,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid var(--border-soft)", fontSize: 12 }} />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={64}>
                        <LabelList dataKey="value" position="top" style={{ fontSize: 12, fontWeight: 800, fill: "#181818" }} />
                        {[0, 1].map((i) => <Cell key={i} fill={i === 0 ? "#EE722E" : "#2F6FED"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top / Flop */}
            <div className="grid gap-4 @3xl/page:grid-cols-2">
              <TopFlopCard title="Top spots" subtitle="Meilleur rendement (bulletins/jour)" icon={Trophy} accent="#1F9D55" rows={topflop?.top || []} navigate={navigate} testId="top-spots" />
              <TopFlopCard title="Flop spots" subtitle="Rendement le plus faible — à surveiller" icon={AlertTriangle} accent="#D64545" rows={topflop?.flop || []} navigate={navigate} testId="flop-spots" flop />
            </div>

            {/* Décisions + Brief */}
            <div className="grid gap-4 @4xl/page:grid-cols-3">
              <DecisionsPanel decisions={decisions} telAlerts={telAlerts} navigate={navigate} />
              <BriefCard brief={brief} />
            </div>

            {/* Status distribution */}
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]">
              <h3 className="text-sm font-bold">Répartition des statuts</h3>
              <div className="mt-3 grid gap-x-6 gap-y-2 @2xl/page:grid-cols-2">
                {Object.entries(ov?.status_counts || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => {
                  const meta = SPOT_STATUS[k] || { label: ov?.status_labels?.[k] || k };
                  const pct = Math.round((v / (totalSpots || 1)) * 100);
                  return (
                    <button key={k} onClick={() => navigate(`/spots?status=${k}`)} className="w-full" data-testid={`dist-${k}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold">{meta.label}</span>
                        <span className="text-muted-ink">{v}</span>
                      </div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[rgba(24,24,24,0.06)]">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full bg-[var(--shariti-orange)]" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid gap-4 @3xl/page:grid-cols-3">
              <QuickCard icon={Store} title="Banque de spots" desc="Rechercher, filtrer et ouvrir les fiches spots." onClick={() => navigate("/spots")} testId="quick-spots" />
              <QuickCard icon={CalendarDays} title="Planning des lignes" desc="Vue hebdomadaire, glisser-déposer, règles de rotation." onClick={() => navigate("/planning")} testId="quick-planning" />
              {user?.role === "admin"
                ? <QuickCard icon={TrendingUp} title="Importer la BDD" desc="Charger le fichier Excel et valider le rapport d'import." onClick={() => navigate("/import")} testId="quick-import" />
                : <QuickCard icon={PhoneCall} title="Prospection" desc="Votre file d'appels et l'assistant." onClick={() => navigate("/prospection")} testId="quick-prospection" />}
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}

function ZoneMapCard({ data, navigate, isAdmin }) {
  const [dlId, setDlId] = useState(null);

  const downloadExcel = async (cdp) => {
    setDlId(cdp.id);
    try {
      const res = await api.get(`/dashboard/zone-export?cdp_id=${cdp.id}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `Spots_${cdp.name}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Liste de phoning de ${cdp.name} téléchargée`);
    } catch (e) {
      toast.error("Échec de l'export Excel");
    } finally {
      setDlId(null);
    }
  };

  if (!data) return null;
  const myId = data.my_cdp_id;
  const cdps = data.cdps || []; // déjà filtré côté serveur : CDP actifs uniquement
  const reassign = data.reassign_zones || [];
  const freeZones = data.free_zones || [];

  const ZonePill = ({ z, muted = false, testid }) => {
    const st = ZONE_STATUS[z.status] || ZONE_STATUS.occupee;
    const depts = (z.departments || []).join(", ");
    const alert = z.status === "a_renouveler" || z.status === "bientot_libre";
    const title = z.former_cdp_name
      ? `${z.name} (${depts || "—"}) · ancien titulaire : ${z.former_cdp_name} — à réattribuer`
      : z.status === "a_renouveler"
      ? `${z.name} (${depts || "—"}) · rotation à renouveler`
      : z.status === "bientot_libre"
      ? `${z.name} (${depts || "—"}) · bientôt libre (${z.days_remaining} j)`
      : `${z.name} · ${z.spot_count} spot(s)`;
    return (
      <button onClick={() => navigate(`/spots?territoire=${z.id}`)}
        data-testid={testid} title={title}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)] ${muted ? "border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink" : alert ? "border-[rgba(214,69,69,0.35)] bg-white" : "border-[var(--border-soft)] bg-white"}`}>
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: muted ? ZONE_STATUS.libre.dot : st.dot }} />
        <span>{z.name}</span>
        {depts && <span className="font-medium text-muted-ink">· {depts}</span>}
        {z.is_mutualise && <Users className="h-2.5 w-2.5 text-[#4338CA]" />}
      </button>
    );
  };

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid="zone-map-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(238,114,46,0.12)]">
            <Map className="h-4 w-4 text-[var(--shariti-orange)]" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Territoires — qui occupe quelle zone ?</h3>
            <p className="text-[11px] text-muted-ink">
              {data.covered_zones}/{data.total_zones} zones couvertes par l'effectif actif
              {reassign.length > 0 && <span> · <span className="font-semibold text-[#9B2226]">{reassign.length} à réattribuer</span></span>}
            </p>
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => navigate("/admin")} data-testid="zone-map-manage"
            className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-muted-ink transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]">
            <RefreshCw className="h-3.5 w-3.5" /> Gérer les zones
          </button>
        )}
      </div>

      {cdps.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-6 text-center text-xs text-muted-ink" data-testid="zone-map-empty">
          Aucun CDP actif pour le moment.
        </div>
      ) : (
        <div className="mt-4 grid gap-3 @2xl/page:grid-cols-2 @5xl/page:grid-cols-3">
          {cdps.map((c) => {
            const mine = c.id === myId;
            return (
              <div key={c.id} data-testid={`zone-cdp-${c.id}`}
                className={`rounded-xl border bg-[var(--surface-2)] p-3 transition-colors ${mine ? "border-[var(--shariti-orange)] ring-1 ring-[rgba(238,114,46,0.35)]" : "border-[var(--border-soft)]"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                    <span className="break-words text-sm font-bold">{c.name}</span>
                    {mine && <span className="shrink-0 rounded-full bg-[var(--shariti-orange)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white" data-testid="zone-cdp-you">Vous</span>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-[10px] font-semibold text-muted-ink">
                    <span className="inline-flex items-center gap-0.5" title="Zones attribuées"><MapPin className="h-3 w-3" />{c.zone_count}</span>
                    <span className="inline-flex items-center gap-0.5" title="Spots rattachés"><Store className="h-3 w-3" />{c.spot_count}</span>
                    <button
                      onClick={() => downloadExcel(c)}
                      disabled={dlId === c.id || c.spot_count === 0}
                      data-testid={`zone-export-${c.id}`}
                      title={`Télécharger la liste de phoning Excel de ${c.name} (spots classés par priorité)`}
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-1.5 py-1 font-bold text-[var(--shariti-orange)] transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)] disabled:cursor-not-allowed disabled:opacity-50">
                      {dlId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                      <span className="hidden @lg/page:inline">Excel</span>
                    </button>
                  </div>
                </div>
                {c.zones.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {c.zones.map((z) => <ZonePill key={z.id} z={z} testid={`zone-pill-${z.id}`} />)}
                  </div>
                ) : c.recovery_mode ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.08)] px-2 py-1.5 text-[10px] font-bold text-[#8A3A10]" data-testid={`zone-recovery-${c.id}`}>
                    <RefreshCw className="h-3 w-3" />Portefeuille de reconquête — toutes zones
                  </div>
                ) : (
                  <div className="mt-2 text-[10px] font-medium text-muted-ink">Aucune zone attribuée — à définir.</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(reassign.length > 0 || freeZones.length > 0) && (
        <div className="mt-3 rounded-xl border border-dashed border-[rgba(214,69,69,0.3)] bg-[rgba(214,69,69,0.04)] p-3" data-testid="zone-map-reassign">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#9B2226]">
            <AlertTriangle className="h-3.5 w-3.5" />
            Zones à réattribuer
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-extrabold">{reassign.length + freeZones.length}</span>
          </div>
          <p className="mt-0.5 text-[10px] text-muted-ink">Titulaire parti ou zone libre — à réaffecter à un CDP actif.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {reassign.map((z) => <ZonePill key={z.id} z={z} muted testid={`zone-reassign-${z.id}`} />)}
            {freeZones.map((z) => <ZonePill key={z.id} z={z} muted testid={`zone-free-${z.id}`} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function DecisionsPanel({ decisions, telAlerts = [], navigate }) {
  const list = decisions?.decisions || [];
  const TEL_ICON = { phone: Phone, "trending-down": TrendingDown, "alert-triangle": AlertTriangle, wallet: Wallet };
  const TEL_SEV = {
    high: { color: "#C0392B", bg: "rgba(192,57,43,0.08)", border: "rgba(192,57,43,0.28)" },
    medium: { color: "#B4531B", bg: "rgba(238,114,46,0.08)", border: "rgba(238,114,46,0.28)" },
  };
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)] @4xl/page:col-span-2" data-testid="decisions-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgba(238,114,46,0.12)]"><ListChecks className="h-4 w-4 text-[var(--shariti-orange)]" /></div>
          <h3 className="text-sm font-bold">Décisions à prendre</h3>
        </div>
        <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-[11px] font-bold text-muted-ink">{list.length + telAlerts.length}</span>
      </div>

      {/* Alertes téléphonie (cockpit) */}
      {telAlerts.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="telephony-alerts">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
            <Phone className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Alertes téléphonie
          </div>
          {telAlerts.map((a, i) => {
            const sev = TEL_SEV[a.severity] || TEL_SEV.medium;
            const Icon = TEL_ICON[a.icon] || Phone;
            return (
              <div key={`${a.type}-${i}`} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: sev.border, background: sev.bg }} data-testid={`telephony-alert-${i}`}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: sev.color }}><Icon className="h-3.5 w-3.5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold leading-tight">{a.title}</span>
                    {a.demo && <span className="rounded-full bg-[rgba(252,219,83,0.5)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#5A4200]">Démo</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-[rgba(24,24,24,0.66)]">{a.detail}</p>
                  <button onClick={() => navigate(a.action_href)} data-testid={`telephony-alert-action-${i}`}
                    className="mt-2 inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-bold shadow-[var(--shadow-sm)] transition-transform hover:-translate-y-0.5" style={{ color: sev.color }}>
                    {a.action_label} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {list.length === 0 && telAlerts.length === 0 ? (
        <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(31,157,85,0.12)]"><Target className="h-6 w-6 text-[#1F9D55]" /></div>
          <p className="mt-3 text-sm font-semibold">Tout est sous contrôle</p>
          <p className="text-xs text-muted-ink">Aucune action prioritaire détectée cette semaine.</p>
        </div>
      ) : list.length > 0 ? (
        <div className="mt-4 space-y-2.5">
          {telAlerts.length > 0 && <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink"><ListChecks className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Priorités de la semaine</div>}
          {list.map((r) => {
            const sev = SEV[r.severity] || SEV.low;
            return (
              <motion.div key={`${r.type}-${r.priority}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: r.priority * 0.03 }}
                className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: sev.border, background: sev.bg }} data-testid={`decision-${r.priority}`}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-extrabold text-white" style={{ background: sev.color }}>{r.priority}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold leading-tight">{r.title}</span>
                    {r.impact_bulletins > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold" style={{ color: sev.color }} data-testid={`decision-impact-${r.priority}`}>
                        <Zap className="h-3 w-3" /> +{r.impact_bulletins} bulletins
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-[rgba(24,24,24,0.66)]">{r.detail}</p>
                  <button onClick={() => navigate(r.action_href)} data-testid={`decision-action-${r.priority}`}
                    className="mt-2 inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-bold shadow-[var(--shadow-sm)] transition-transform hover:-translate-y-0.5" style={{ color: sev.color }}>
                    {r.action_label} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function BriefCard({ brief }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(brief?.text || "");
      toast.success("Brief copié dans le presse-papiers");
    } catch (err) {
      console.warn("Clipboard write failed:", err);
      toast.error("Impossible de copier dans le presse-papiers");
    }
  };
  return (
    <div className="flex flex-col rounded-2xl border border-[var(--border-soft)] bg-[var(--shariti-ink)] p-5 text-white shadow-[var(--shadow-sm)]" data-testid="brief-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold">Brief du lundi</h3>
          <p className="text-[11px] text-white/60">Semaine {brief?.week_number}</p>
        </div>
        <button onClick={copy} data-testid="brief-copy" className="flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-[11px] font-semibold hover:bg-white/20"><Copy className="h-3.5 w-3.5" /> Copier</button>
      </div>
      <div className="mt-3 space-y-2">
        {(brief?.highlights || []).map((h, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-white/90"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />{h}</div>
        ))}
      </div>
      {(brief?.top_decisions || []).length > 0 && (
        <>
          <div className="mt-4 text-[11px] font-bold uppercase tracking-wide text-white/50">Priorités</div>
          <div className="mt-1.5 space-y-1.5">
            {brief.top_decisions.map((r) => (
              <div key={r.priority} className="flex items-center gap-2 text-xs" data-testid={`brief-priority-${r.priority}`}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[var(--shariti-orange)] text-[10px] font-extrabold">{r.priority}</span>
                <span className="break-words text-white/90">{r.title}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TopFlopCard({ title, subtitle, icon: Icon, accent, rows, navigate, testId, flop }) {
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}18` }}><Icon className="h-4 w-4" style={{ color: accent }} /></div>
        <div><h3 className="text-sm font-bold">{title}</h3><p className="text-[11px] text-muted-ink">{subtitle}</p></div>
      </div>
      <div className="mt-3 space-y-1.5">
        {rows.length === 0 && <div className="py-6 text-center text-xs text-muted-ink">Pas assez d'historique pour classer les spots.</div>}
        {rows.map((s) => (
          <button key={s.id} onClick={() => navigate(`/spots/${s.id}`)} data-testid={`${testId}-row-${s.id}`}
            className="flex w-full items-center gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-left transition-colors hover:border-[var(--shariti-orange)]">
            <div className="min-w-0 flex-1">
              <div className="break-words text-xs font-bold">{s.name}</div>
              <div className="flex items-center gap-2 text-[10px] text-muted-ink">
                <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{s.ville} {s.dept ? `(${s.dept})` : ""}</span>
                {s.next_available && <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> dispo {frenchDate(s.next_available)}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-xs font-extrabold" style={{ color: accent }}>
                {flop ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                {s.bulletins != null ? `${s.bulletins}` : "—"} <span className="font-semibold text-muted-ink">bult.</span>
              </div>
              <div className="text-[10px] text-muted-ink">{s.moy_jour ?? "—"}/jour · {s.journees ?? 0} j</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuickCard({ icon: Icon, title, desc, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="card-hover group flex items-start gap-4 rounded-2xl border border-[var(--border-soft)] bg-white p-5 text-left shadow-[var(--shadow-sm)]">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--surface-2)]">
        <Icon className="h-5 w-5 text-[var(--shariti-orange)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 font-bold">{title}<ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" /></div>
        <p className="mt-0.5 text-xs text-muted-ink">{desc}</p>
      </div>
    </button>
  );
}
